import { useMemo, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  DashboardPageFormBody,
  DashboardPageFormField,
  DashboardPageFormToggleSection,
} from '@shigo-ui/dashboard-ui';
import {
  AutocompleteSingleSelect,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SimpleMultiSelect,
  Textarea,
} from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import { useDemandPartnerOptionsQuery } from '../../../partners/hooks';
import {
  IntegrationMediaType,
  IntegrationStatus,
  integrationMediaTypeLabel,
  integrationStatusLabel,
  type Integration,
} from '../../model';
import type { IntegrationFormValue } from '../../integrationFormSchema';
import { IntegrationMappings } from './IntegrationMappings';
import { IntegrationInlineSwitch, IntegrationRequestPolicy } from './IntegrationRequestPolicy';

export function IntegrationGeneralFields(props: {
  editing: boolean;
  initialIntegration?: Integration | undefined;
}) {
  // react-hook-form subscriptions rely on runtime field updates that React Compiler memoization
  // does not currently preserve for this component.
  'use no memo';
  const { control, register, formState, setValue } = useFormContext<IntegrationFormValue>();
  const duplicateUsesDefaultFloor = useWatch({ control, name: 'dupUseDefaultFloor' });
  const [search, setSearch] = useState('');
  const selectedId = props.initialIntegration?.demandPartner.id;
  const partners = useDemandPartnerOptionsQuery(search, selectedId);
  const options = useMemo(
    () =>
      (partners.data?.content ?? []).map((partner) => ({
        id: partner.id,
        label: partner.displayName ?? partner.name ?? partner.id,
      })),
    [partners.data?.content],
  );

  return (
    <DashboardPageFormBody
      width="lg"
      title={dashboardT('integrations.sections.general')}
      description={dashboardT('integrations.sections.generalDescription')}
    >
      <div className="grid gap-5 md:grid-cols-2">
        <Controller
          control={control}
          name="demandPartnerId"
          render={({ field, fieldState }) => (
            <DashboardPageFormField
              label={dashboardT('integrations.demandPartner')}
              required
              error={fieldState.error?.message}
            >
              <AutocompleteSingleSelect
                label={dashboardT('integrations.demandPartner')}
                value={field.value}
                options={options}
                onValueChange={field.onChange}
                onSearchTermChange={setSearch}
                searchDebounceMs={650}
                minSearchLength={1}
                loading={partners.isFetching}
                disabled={props.editing}
                placeholder={dashboardT('integrations.selectDemandPartner')}
                searchPlaceholder={dashboardT('integrations.searchDemandPartners')}
                emptyMessage={dashboardT('integrations.noDemandPartners')}
              />
            </DashboardPageFormField>
          )}
        />
        <DashboardPageFormField
          label={dashboardT('integrations.name')}
          htmlFor="integration-name"
          required
          error={formState.errors.name?.message}
        >
          <Input
            id="integration-name"
            required
            aria-invalid={Boolean(formState.errors.name)}
            {...register('name')}
          />
        </DashboardPageFormField>
        <Controller
          control={control}
          name="status"
          render={({ field }) => (
            <DashboardPageFormField label={dashboardT('integrations.status')} required>
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger aria-label={dashboardT('integrations.status')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(IntegrationStatus).map((status) => (
                    <SelectItem key={status} value={status}>
                      {integrationStatusLabel(status)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </DashboardPageFormField>
          )}
        />
        <Controller
          control={control}
          name="mediaTypes"
          render={({ field, fieldState }) => (
            <DashboardPageFormField
              label={dashboardT('integrations.mediaTypes')}
              required
              error={fieldState.error?.message}
              hint={dashboardT('integrations.hints.mediaTypes')}
            >
              <SimpleMultiSelect
                label={dashboardT('integrations.mediaTypes')}
                options={Object.values(IntegrationMediaType).map((mediaType) => ({
                  id: mediaType,
                  label: integrationMediaTypeLabel(mediaType),
                }))}
                value={field.value}
                onValueChange={field.onChange}
                placeholder={dashboardT('integrations.selectMediaTypes')}
                emptyMessage={dashboardT('integrations.noMediaTypes')}
              />
            </DashboardPageFormField>
          )}
        />
        <DashboardPageFormField
          label={dashboardT('integrations.sellerDomain')}
          htmlFor="integration-schain-asi"
        >
          <Input id="integration-schain-asi" {...register('schainAsi')} />
        </DashboardPageFormField>
        <DashboardPageFormField
          label={dashboardT('integrations.sellerId')}
          htmlFor="integration-seller-id"
          hint={dashboardT('integrations.hints.sellerId')}
        >
          <Input id="integration-seller-id" {...register('sellerId')} />
        </DashboardPageFormField>
      </div>

      <IntegrationRequestPolicy />

      <Controller
        control={control}
        name="multipleImpressionsEnabled"
        render={({ field }) => (
          <DashboardPageFormToggleSection
            label={dashboardT('integrations.multipleImpressions')}
            description={dashboardT('integrations.sections.multipleImpressionsDescription')}
            checked={field.value}
            onCheckedChange={field.onChange}
          >
            <DashboardPageFormField
              label={dashboardT('integrations.multipleImpressionsCount')}
              htmlFor="integration-impression-count"
              error={formState.errors.multipleImpressionsCount?.message}
              hint={dashboardT('integrations.hints.multipleImpressionsCount')}
            >
              <Input
                id="integration-impression-count"
                type="number"
                min="1"
                max="5"
                {...register('multipleImpressionsCount', { valueAsNumber: true })}
              />
            </DashboardPageFormField>
          </DashboardPageFormToggleSection>
        )}
      />

      <Controller
        control={control}
        name="duplicateDemandIntegration"
        render={({ field }) => (
          <DashboardPageFormToggleSection
            label={dashboardT('integrations.sections.duplication')}
            description={dashboardT('integrations.sections.duplicationDescription')}
            checked={field.value}
            onCheckedChange={(checked) => {
              field.onChange(checked);
              if (checked) {
                setValue('dupUseDefaultFloor', false, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }
            }}
          >
            <IntegrationInlineSwitch
              name="dupUseDefaultFloor"
              label={dashboardT('integrations.useDefaultFloor')}
              description={dashboardT(
                duplicateUsesDefaultFloor
                  ? 'integrations.hints.useDefaultFloorEnabled'
                  : 'integrations.hints.useDefaultFloorDisabled',
              )}
            />
            <DashboardPageFormField
              label={dashboardT('integrations.dupCount')}
              htmlFor="integration-dupCount"
              error={formState.errors.dupCount?.message}
              hint={dashboardT('integrations.hints.dupCount')}
            >
              <Input
                id="integration-dupCount"
                type="number"
                min="1"
                step="1"
                {...register('dupCount', { valueAsNumber: true })}
              />
            </DashboardPageFormField>
            {!duplicateUsesDefaultFloor ? (
              <div className="grid gap-4 sm:grid-cols-3">
                {(['dupMinFloor', 'dupMaxFloor', 'dupIncrement'] as const).map((name) => (
                  <DashboardPageFormField
                    key={name}
                    label={dashboardT(`integrations.${name}`)}
                    htmlFor={`integration-${name}`}
                    error={formState.errors[name]?.message}
                    hint={
                      name === 'dupIncrement'
                        ? dashboardT('integrations.hints.dupIncrement')
                        : undefined
                    }
                  >
                    <Input
                      id={`integration-${name}`}
                      type="number"
                      min="0"
                      step="any"
                      {...register(name, { valueAsNumber: true })}
                    />
                  </DashboardPageFormField>
                ))}
              </div>
            ) : null}
          </DashboardPageFormToggleSection>
        )}
      />

      <IntegrationMappings integration={props.initialIntegration} />

      <DashboardPageFormField label={dashboardT('integrations.notes')} htmlFor="integration-notes">
        <Textarea id="integration-notes" rows={4} {...register('notes')} />
      </DashboardPageFormField>
    </DashboardPageFormBody>
  );
}
